/**
 * What `/missions` and `/career` do that is not JSX, in the same shape
 * `shell-runtime.ts` gives `/`: one mutable record, plain functions over it,
 * and a single `ShellView` signal for the DOM chrome outside the frame.
 *
 * The two surfaces differ only in *which* screen is on and what a command
 * means, so both hand this runtime a `SurfaceController` and it owns
 * everything else — the canvas, the asset load, the focus cursor, the
 * keyboard and pointer, and the repaint.
 *
 * There is no start gate here. The gate exists because an intro started on
 * route load would play mute; these surfaces have no intro, so they follow the
 * shell's *revisit* path instead — the title theme starts on the first input,
 * once the browser has granted audio.
 *
 * A surface can also ask for a career-milestone cutscene mid-flow (the medal
 * ceremony, the promotion salute, war's end): `SurfaceController.cutscene()`
 * names a baked `.TTM` by filename, and this runtime owns fetching it,
 * parsing it, loading whatever it draws, and running it on the same logical
 * canvas the DrawOp screens paint into (`ShellSurface.logical` — the exact
 * technique `shell-runtime.ts` already uses to hand the intro that canvas).
 * While one is running, ordinary paint/keyboard/pointer handling stands
 * aside; any key or click skips it, exactly like the intro.
 */

import { parseTtm } from "@red-opus/formats";
import { createShellAudio, SHELL_THEME, type ShellAudio } from "./audio/shell-audio.ts";
import { cutscenePreloadSet } from "./cutscene/cutscene-cues.ts";
import { createCutscenePlayer, type CutscenePlayer } from "./cutscene/cutscene-player.ts";
import { ticksFromScript } from "./cutscene/cutscene-ticks.ts";
import { createIntroClock } from "./intro/intro-clock.ts";
import type { MenuAction } from "./model/menu-model.ts";
import { interactiveWidgets, moveDialogFocus, moveFocus, type ShellDialog, type ShellScreen } from "./model/shell-screen.ts";
import { createLogicalSurface } from "./render/canvas-painter.ts";
import type { FontId } from "./render/draw-ops.ts";
import { buildSurfaceView } from "./render/shell-view.ts";
import { loadShellArtwork, loadShellFonts, type ShellAssetRequest, type ShellAssets } from "./render/shell-assets.ts";
import { createShellSurface, type CssRect, type ShellSurface } from "./render/shell-surface.ts";
import type { FontAtlas } from "./render/text-metrics.ts";

/** What a surface asks the runtime to play instead of its normal screen. */
export interface CutsceneRequest {
  /** Stable across repeated `cutscene()` calls for "the same scene still wanted" — changing it starts a new load. */
  readonly id: string;
  /** `data/baked/cutscenes/<ttm>` (`tools/bake/src/stages/cutscene-scripts.ts`). */
  readonly ttm: string;
  /** `MUSIC_PATHS` key this script's own `LOAD SONG` resolves to — see `cutscene-cues.ts`'s header for why it can't be derived from the ticks alone. */
  readonly song: string;
}

export interface SurfaceController {
  /** Everything off `/baked` this surface can reach for, requested once. */
  readonly assets: ShellAssetRequest;
  screen(fonts: ReadonlyMap<FontId, FontAtlas>): ShellScreen;
  dialog(): ShellDialog | undefined;
  focus(): number;
  setFocus(index: number): void;
  setDialog(dialog: ShellDialog | undefined): void;
  /** Runs an action and returns a path when the surface wants the router to leave. */
  run(action: MenuAction | undefined): string | undefined;
  /** Consumes a printable key for a text field. `false` leaves it to navigation. */
  typeKey(key: string): boolean;
  /** A cutscene to play right now instead of `screen()`, or `undefined` for none. Optional: most surfaces have none. */
  cutscene?(): CutsceneRequest | undefined;
  /** Called once the cutscene named by the last `cutscene()` has finished or been skipped. */
  cutsceneFinished?(): void;
  /**
   * Whether the current screen has to watch something that changes without an
   * input — a joystick, which the Gamepad API only reports by polling
   * (`shell/controls-surface.ts`). Optional: most surfaces are pure functions
   * of a click and never need a clock.
   */
  needsPolling?(): boolean;
  /** One poll. Returns whether the screen changed and wants a repaint. */
  poll?(): boolean;
}

export type ScreenStatus = "loading" | "ready" | "failed";

/** How a surface differs from `/missions` and `/career`, which want every default. */
export interface ScreenRuntimeOptions {
  /**
   * Whether the title theme starts on the first input, as it does on every
   * route that *is* the shell. `false` for a panel painted over something else
   * that is already making noise — `flight/PreferencesOverlay.tsx` opens over a
   * running sortie, and a title theme fading up over the engine is not what
   * pressing `O` asks for. It suppresses `unlock()` outright rather than
   * muting it, so no second `AudioContext` is created either.
   */
  readonly music?: boolean;
  /**
   * Composite the frame over what is behind the canvas rather than over black
   * (`render/shell-surface.ts`). `true` only for G38's in-flight `ESC` board,
   * which the original draws over the cockpit; every other in-flight sheet in
   * this port deliberately replaces the picture.
   */
  readonly transparent?: boolean;
}

interface RuntimeState {
  readonly controller: SurfaceController;
  readonly surface: ShellSurface;
  readonly audio: ShellAudio;
  readonly music: boolean;
  readonly navigate: (path: string) => void;
  listener: ((status: ScreenStatus) => void) | undefined;
  viewportListener: ((box: CssRect | undefined) => void) | undefined;
  status: ScreenStatus;
  assets: ShellAssets | undefined;
  unlocked: boolean;
  disposed: boolean;
  cutscene: CutscenePlayer | undefined;
  /** The `id` of the request currently loading or playing — `undefined` when none is. */
  cutsceneId: string | undefined;
  /** The polling loop's handle, so `dispose` can cancel it (`pollLoop`). */
  pollHandle: number | undefined;
}

function currentScreen(state: RuntimeState): ShellScreen | undefined {
  return state.assets ? state.controller.screen(state.assets.fonts) : undefined;
}

/**
 * Fetches the cutscene's raw `.TTM`, decodes what it draws, folds that into
 * the surface's own assets (additively — `loadShellArtwork` merges rather
 * than replaces, so the screens the surface paints once the scene ends are
 * still there), and starts it on the same logical canvas the DrawOp screens
 * use. Every await re-checks `cutsceneId` against `request.id`: a second
 * `cutscene()` call (or a dispose) while this is still in flight must not
 * let a stale load clobber whatever is current by the time it resolves.
 */
async function startCutscene(state: RuntimeState, request: CutsceneRequest): Promise<void> {
  state.cutsceneId = request.id;
  const response = await fetch(`/baked/cutscenes/${request.ttm}`);
  if (state.disposed || state.cutsceneId !== request.id || !state.assets) return;
  if (!response.ok) {
    state.cutsceneId = undefined;
    state.controller.cutsceneFinished?.();
    paint(state);
    return;
  }
  const script = parseTtm(new Uint8Array(await response.arrayBuffer()));
  const ticks = ticksFromScript(script);
  const preload = cutscenePreloadSet(ticks, [request.song]);
  state.assets = await loadShellArtwork(state.assets, {
    images: preload.images,
    sprites: preload.sprites,
    palettes: preload.palettes,
  });
  if (state.disposed || state.cutsceneId !== request.id || !state.assets) return;
  await state.audio.preload(preload.audio);
  if (state.disposed || state.cutsceneId !== request.id || !state.assets) return;
  const player = createCutscenePlayer({
    scene: createLogicalSurface(),
    target: state.surface.logical,
    assets: state.assets,
    audio: state.audio,
    clock: createIntroClock(),
    onFrame: () => state.surface.blit(),
    onFinished: () => {
      state.cutscene = undefined;
      state.cutsceneId = undefined;
      state.controller.cutsceneFinished?.();
      paint(state);
    },
  });
  state.cutscene = player;
  player.start(ticks);
}

/** Picks up a newly-requested cutscene; a repeated request for the same `id` (already loading or playing) is left alone. */
function syncCutscene(state: RuntimeState): void {
  const request = state.controller.cutscene?.();
  if (!request || state.cutsceneId === request.id) return;
  void startCutscene(state, request);
}

/**
 * The theme under whatever is on screen. `playMusic` is a no-op when the named
 * track is already the one playing, so this can run on every paint; the effect
 * is that a screen naming its own music takes over while it is up and the shell
 * theme comes back by itself when the flow moves on.
 */
function syncMusic(state: RuntimeState, screen: ShellScreen): void {
  if (!state.music || !state.unlocked) return;
  state.audio.playMusic(screen.music ?? SHELL_THEME, true);
}

function paint(state: RuntimeState): void {
  syncCutscene(state);
  if (state.cutscene?.running()) return;
  const screen = currentScreen(state);
  if (!state.assets || !screen) return;
  syncMusic(state, screen);
  const view = buildSurfaceView(screen, state.controller.focus(), state.controller.dialog(), state.assets.fonts);
  state.surface.paint(state.assets, view.ops);
  // After the blit, so the letterbox transform the inset is mapped through is
  // the one that was just used. A screen with no 3D hole reports `undefined`,
  // which is how the overlay unmounts on the way back to a plain screen.
  state.viewportListener?.(screen.viewport ? state.surface.cssRectOf(screen.viewport) : undefined);
}

function commit(state: RuntimeState, action: MenuAction | undefined): void {
  const path = state.controller.run(action);
  paint(state);
  if (path !== undefined) state.navigate(path);
}

function activate(state: RuntimeState): void {
  const dialog = state.controller.dialog();
  if (dialog) {
    commit(state, dialog.rows[dialog.focus]?.action);
    return;
  }
  const screen = currentScreen(state);
  if (!screen) return;
  commit(state, focusedAction(screen, state.controller.focus()));
}

/** A disabled button keeps its place in the focus order so the cursor can rest on it, but activating it does nothing. */
function focusedAction(screen: ShellScreen, focus: number): MenuAction | undefined {
  const control = interactiveWidgets(screen)[focus];
  if (!control) return undefined;
  if ((control.kind === "button" || control.kind === "toggle" || control.kind === "value") && !control.enabled) return undefined;
  return control.action;
}

function step(state: RuntimeState, delta: number): void {
  const dialog = state.controller.dialog();
  if (dialog) {
    state.controller.setDialog(moveDialogFocus(dialog, delta));
    paint(state);
    return;
  }
  const screen = currentScreen(state);
  if (!screen) return;
  state.controller.setFocus(moveFocus(screen, state.controller.focus(), delta));
  paint(state);
}

function cancel(state: RuntimeState): void {
  const dialog = state.controller.dialog();
  if (dialog) {
    state.controller.setDialog(undefined);
    paint(state);
    return;
  }
  commit(state, currentScreen(state)?.cancel);
}

/** The revisit path: the title theme waits for whatever the first input turns out to be. */
async function unlock(state: RuntimeState): Promise<void> {
  if (state.unlocked || !state.music) return;
  state.unlocked = true;
  await state.audio.unlock();
  const screen = currentScreen(state);
  state.audio.playMusic(screen?.music ?? SHELL_THEME, true);
}

function keyDown(state: RuntimeState, event: KeyboardEvent): void {
  if (state.status !== "ready") return;
  void unlock(state);
  if (state.cutscene?.running()) {
    event.preventDefault();
    state.cutscene.skip();
    return;
  }
  if (event.key === " " || event.key.startsWith("Arrow")) event.preventDefault();
  if (event.key === "ArrowUp" || event.key === "ArrowLeft") return step(state, -1);
  if (event.key === "ArrowDown" || event.key === "ArrowRight") return step(state, 1);
  if (event.key === "Enter") return activate(state);
  if (event.key === "Escape") return cancel(state);
  if (state.controller.typeKey(event.key)) {
    event.preventDefault();
    paint(state);
  }
}

function pointerHit(state: RuntimeState, event: PointerEvent) {
  const point = state.surface.logicalPointAt(event.clientX, event.clientY);
  const screen = currentScreen(state);
  if (!point || !state.assets || !screen) return undefined;
  return buildSurfaceView(screen, state.controller.focus(), state.controller.dialog(), state.assets.fonts).hitTest(point.x, point.y);
}

function pointerDown(state: RuntimeState, event: PointerEvent): void {
  if (state.status !== "ready") return;
  void unlock(state);
  if (state.cutscene?.running()) {
    state.cutscene.skip();
    return;
  }
  const hit = pointerHit(state, event);
  if (!hit) return;
  const dialog = state.controller.dialog();
  if (hit.kind === "dialog" && dialog) {
    state.controller.setDialog({ ...dialog, focus: hit.index });
    commit(state, dialog.rows[hit.index]?.action);
    return;
  }
  const screen = currentScreen(state);
  if (!screen) return;
  state.controller.setFocus(hit.index);
  commit(state, focusedAction(screen, hit.index));
}

function pointerMove(state: RuntimeState, event: PointerEvent): void {
  if (state.status !== "ready" || state.cutscene?.running()) return;
  const hit = pointerHit(state, event);
  if (!hit) return;
  const dialog = state.controller.dialog();
  if (hit.kind === "dialog" && dialog) state.controller.setDialog({ ...dialog, focus: hit.index });
  else state.controller.setFocus(hit.index);
  paint(state);
}

/**
 * The device-polling loop, for the one surface that needs one: the CONTROLS
 * screen has to show a stick moving, and the Gamepad API has no events to
 * subscribe to.
 *
 * It runs for as long as the runtime is mounted rather than being started and
 * stopped as the screen changes, because there is no reactive edge to hang that
 * on — a view change happens inside `controller.run`, which is a plain method
 * call. The cost of that is one `needsPolling()` — a boolean field read — per
 * animation frame, and the alternative is a second lifecycle to get wrong.
 */
function pollLoop(state: RuntimeState): void {
  if (state.disposed) return;
  state.pollHandle = requestAnimationFrame(() => pollLoop(state));
  if (state.status !== "ready" || state.controller.needsPolling?.() !== true) return;
  if (state.controller.poll?.() === true) paint(state);
}

async function boot(state: RuntimeState): Promise<void> {
  const fonts = await loadShellFonts();
  if (state.disposed) return;
  state.assets = await loadShellArtwork(fonts, state.controller.assets);
  if (state.disposed) return;
  state.status = "ready";
  state.listener?.(state.status);
  paint(state);
}

export interface ScreenRuntime {
  status(): ScreenStatus;
  subscribe(listener: (status: ScreenStatus) => void): void;
  mount(canvas: HTMLCanvasElement | undefined): void;
  keyDown(event: KeyboardEvent): void;
  pointerDown(event: PointerEvent): void;
  pointerMove(event: PointerEvent): void;
  /** Repaints after the controller's own state changed outside an input (a store write, a resize). */
  refresh(): void;
  /** Where the current screen's 3D inset belongs, reported after every paint. */
  onViewport(listener: (box: CssRect | undefined) => void): void;
  dispose(): void;
}

export function createScreenRuntime(
  controller: SurfaceController,
  navigate: (path: string) => void,
  options: ScreenRuntimeOptions = {},
): ScreenRuntime {
  const state: RuntimeState = {
    controller,
    surface: createShellSurface({ transparent: options.transparent === true }),
    audio: createShellAudio(),
    music: options.music !== false,
    navigate,
    listener: undefined,
    viewportListener: undefined,
    status: "loading",
    assets: undefined,
    unlocked: false,
    disposed: false,
    cutscene: undefined,
    cutsceneId: undefined,
    pollHandle: undefined,
  };

  return {
    status: () => state.status,
    subscribe(listener) {
      state.listener = listener;
    },
    mount(canvas) {
      state.surface.attach(canvas);
      if (state.controller.poll) pollLoop(state);
      void boot(state).catch(() => {
        state.status = "failed";
        state.listener?.(state.status);
      });
    },
    keyDown: (event) => keyDown(state, event),
    pointerDown: (event) => pointerDown(state, event),
    pointerMove: (event) => pointerMove(state, event),
    refresh: () => paint(state),
    onViewport(listener) {
      state.viewportListener = listener;
    },
    dispose() {
      state.disposed = true;
      if (state.pollHandle !== undefined) cancelAnimationFrame(state.pollHandle);
      state.cutscene?.stop();
      state.audio.dispose();
    },
  };
}
